import { Injectable } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';

/* eslint-disable max-len */
@Injectable({
  providedIn: 'root',
})
export class DefaultTranslationProvider implements MissingTranslationHandler {
  // We need default translations for UI elements that are shared between sites that don't have
  // translations yet or that are accessible by anonymous users.
  readonly defaultTranslations: {[key: string]: string} = {
    privacyLink: 'Privacy',
    legalLink: 'Legal',
    sysRequirementsLink: 'System Requirements',
    techSupportLink: 'Technical Support',
    helpLink: 'Help',
    myAccountLink: 'My Account',
    chooseLanguageLink: 'Choose Language',
    logoutLink: 'Logout',

    'header-HLLogo': 'Highlights Portal logo return to Overview',
    'header-ZBLogo': 'ZB Portal logo return to Overview',
    'toast-savedLocale-success': 'Successfully saved your locale',
    'toast-savedLocale-error': 'Failed to update language preference.',
    'header-hasSubmenu': 'has submenu',
    'header-chooseRole': 'Choose Role',
    'header-noClassroom': 'No Classrooms',
    'parent-subnav-progressReportsLink': 'Progress Reports',
    'parent-subnav-reportsLink': 'Progress Reports',
    'parent-subnav-studentActivityReportsLink': 'Student Activity Reports',
    'parent-subnav-classUpdatesLink': 'Class Updates',
    'parent-subnav-resourceRepositoryLink': 'Resource Repository',
    'parent-subnav-studentAssignmentsLink': 'Student Assignments',
    'parent-subnav-zbpForStudentsLink': 'ZB Portal For Students',
    'parent-subHeading-schoolYear': 'School Year',
    'parent-pageTitle-classUpdates': 'Class Updates',
    'parent-pageTitle-homePage': 'Parent Homepage',
    'parent-teacherMessage-title': 'Message From The Teacher:',
    'parent-pageWelcome': 'Welcome to MyZBPortal.com!',
    'parent-noStudents-section1': '<p>As a parent or guardian, you have access to scores from your child’s online activities, as well as assignments and messaging from your child’s teacher.</p><p>There are currently no students associated with your account.</p><p>You need at least one student associated with your account to be able to use MyZBPortal.com.<br>Please talk to your child’s teacher about adding them to your account.</p>',
    'parent-noClassrooms-section1': '<p>As a parent or guardian, you have access to scores from your child’s online activities, as well as assignments and messaging from your child’s teacher.</p><p>Your child is currently not in a classroom.</p><p>Your child needs to be in at least one classroom to be able to use MyZBPortal.com. Please talk to your  child’s teacher about adding your child to a classroom.</p>',
    'parent-noLicenses-section1': '<p>For this class, your child does not currently have a digital license assigned for this school year.</p><p>If you believe you are receiving this message in error, please contact your child’s teacher.</p>',
    'parent-select-language': 'Please select your language.',
    'select-language': 'Select Language',
    'parent-login-as-student': `Successfully logged in as`,
    'zbpStudents-parentDashboard': `<p>Your child has been given access to digital resources in ZB Portal for Students. They can log into this area from {{ siteUrl }}.</p><p>To log into {{ siteUrl }}, your child will need the following:<ul><li><strong>Student's username</strong> - {{ username }}</li><li><strong>Student's password</strong> - <em>provided by their school</em></li><li><strong>Student's school ID</strong> - {{ schoolid }}</li></ul></p><p>If your student does not know their password, please contact their teacher for assistance. Your child's teacher can reset their password.</p><p>If your student needs access to ZB Portal and you are not able to contact their teacher, you may click the link below to log into ZB Portal for your child.</p><p><em>Please note: logging in for your child will log you out of the Parent Portal.</em></p>`,
    'shared-materials': 'Materials',
    'shared-no-materials': 'There are no materials for this product.',
    'parent-resource-repository': 'Resource Repository',
    'parent-view-product': 'View Product',
    'parent-no-resources': 'There are no resources available for this product.',
    'parent-report-name-field': 'Report',
    'parent-report-class-field': 'Class',
    'parent-report-product-field': 'Product',
    'parent-report-student-field': 'Student',
    'parent-report-clear-product': 'Clear Product',
    'parent-report-all-products': 'All Products',
    'parent-report-all-classes': 'All Classes',
    'parent-report-apply': 'Apply',
    'parent-report-startDate': 'From (Start Date)',
    'parent-report-startDate-aria': 'From (Start Date) Open calendar',
    'parent-report-endDate': 'To (End Date)',
    'parent-report-endDate-aria': 'To (End Date) Open calendar',
    'parent-report-noDataMessage-multiple': 'Please make the appropriate selections and hit Apply to see the report.',
    'parent-report-noDataMessage-single': 'Please hit apply to see the report.',
    'shared-materials-choose-variant-header': 'Choose an Option',
    'shared-materials-choose-variant-body': 'Select an option below',
    'shared-student-activity-report-activity': 'Activity',
    'shared-student-activity-report-timeCompleted': 'Date/Time Completed',
    'shared-student-activity-report-class': 'Class',
    'shared-student-activity-report-activityType': 'Activity Type',
    'shared-student-activity-report-product': 'Product',
    'shared-student-activity-report-quest': 'Quest',
    'shared-student-activity-report-scoreTimeSpent': 'Score/Time Spent',
    'shared-student-activity-report-noResults': 'No results found',
    'shared-student-activity-report-loadingClassrooms': 'loading classrooms',
    'shared-student-activity-report-loadingStudents': 'loading students',
    'shared-student-activity-report-loadingProducts': 'loading products',
    'shared-student-activity-report-sr-resultsMessage': `results found`,
    'shared-student-activity-report-sr-resultsMessage-single': `result found`,
    'shared-student-activity-report-sr-filtersMessage': `filters applied`,
    'shared-student-activity-report-sr-filtersMessage-single': `filter applied`,
    'shared-student-over-time-report-label': 'Student Over Time Report Table',
    'shared-student-over-time-report-column-test': 'Test',
    'shared-student-over-time-report-column-student-scores-and-status': 'Student Scores and Status',
    'shared-button-close': 'Close',
    'shared-button-save': 'Save',
    'shared-score-detail-header': 'SCORE DETAIL',
    'shared-score-detail-label-edit': 'edit score',
    'shared-score-detail-label-print': 'print score',
    'shared-score-detail-field-student': 'Student',
    'shared-score-detail-field-class': 'Class',
    'shared-score-detail-field-product': 'Product',
    'shared-score-detail-field-activity': 'Activity',
    'shared-score-detail-field-date-time-completed': 'Date/Time Completed',
    'shared-score-detail-field-date-time-completed-empty-message': 'Complete when all parts are scored',
    'shared-score-detail-field-last-overridden': `Score manually overridden by {{ userName }} on {{ overriddenDate }}`,
    'shared-score-detail-column-part': 'Part',
    'shared-score-detail-column-objective': 'Skill Description',
    'shared-score-detail-column-score': 'Score',
    'shared-score-detail-column-benchmark': 'Benchmark',
    'shared-score-detail-column-time-spent': 'Time Spent',
    'shared-score-detail-column-collapsible-label': 'additional details',
    'shared-score-detail-collapsible-column-prompt': 'Prompt',
    'shared-score-detail-collapsible-column-prompt-scoring': 'Prompt and Scoring',
    'shared-score-detail-collapsible-column-answer': 'Student Answer',
    'shared-score-detail-collapsible-column-choices': 'Answer Choices',
    'shared-score-detail-collapsible-column-correct': 'Correct',
    'shared-score-detail-collapsible-column-page': 'Page',
    'shared-score-detail-collapsible-column-time': 'Time Spent',
    'shared-score-detail-collapsible-column-standards': 'Standards',
    'shared-score-detail-collapsible-column-audio-label': 'audio',
    'shared-score-detail-collapsible-definition-not-available': 'Standard(s) description not available.',
    'shared-score-detail-prompt-scoring-link': 'Prompt and Scoring Information',
    'shared-score-detail-icon-correct-label': 'correct',
    'shared-score-detail-icon-incorrect-label': 'incorrect',
    'shared-benchmark-label-below': 'Below',
    'shared-benchmark-label-on-level': 'On Level',
    'shared-benchmark-label-above-level': 'Above Level',
    'shared-benchmark-label-n-a': 'N/A',
    'shared-benchmark-label-challenge': 'Challenge',
    'shared-benchmark-label-emerging': 'Emerging',
    'shared-assessment-status-label-not-started': 'NOT STARTED',
    'shared-assessment-status-label-add-score': '+ ADD SCORE',
    'shared-assessment-status-label-ready-for-scoring': '+ READY FOR SCORING',
    'shared-assessment-status-label-partially-scored': '+ PARTIALLY SCORED',
    'shared-assessment-status-label-in-progress': 'TEST IS CURRENTLY IN PROGRESS',
    'shared-assessment-status-label-scoring-in-progress': 'SCORING IN PROGRESS',
    'shared-assessment-status-label-score-not-available': 'SCORE NOT AVAILABLE',
    'parent-back-to-resource-repo': 'BACK TO RESOURCE REPOSITORY',
    'parent-resource-not-found': 'Resource not found.',
    'parent-variant-not-found': 'Variant not found.',
    'parent-product-line-not-found': 'Product line not found.',
    'myAccount-firstNamePatternToast': 'First name contains invalid characters',
    'myAccount-lastNamePatternToast': 'Last name contains invalid characters',
    'score-detail-print-standards-header': 'Print Standards Descriptions',
    'score-detail-print-standards-message': 'Would you like to include text descriptions of the standards?',
    'score-detail-include-text-toggle': 'Include text descriptions of standards',
    'myAccount-invalidEmailFormat': 'Email format is not valid',
    'parent-report-select-date': 'Select date',
    'parent-report-wordList-field': 'Word List',
    'shared-button-print': 'Print',
    'shared-button-cancel': 'Cancel',
    'shared-spinner-loading': 'Loading',
    'myAccount-showNewPassword': 'Show new password',
    'myAccount-hideNewPassword': 'Hide new password',
    'myAccount-showConfirmPassword': 'Show confirm password',
    'myAccount-hideConfirmPassword': 'Hide confirm password',
  };

  // updated to handle interpolate parameters, if present
  // this is only used when the translation is missing. when the translation is found
  // in the API response the interpolation happens out of the box
  handle(params: MissingTranslationHandlerParams): string {
    const translation = this.defaultTranslations[params.key] || params.key;

    // If there are interpolation parameters, apply them
    if (params.interpolateParams) {
      return this.interpolate(translation, params.interpolateParams);
    }

    return translation;
  }

  private interpolate(translation: string, params: any): string {
    let result = translation;

    Object.entries(params).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      result = result.replace(regex, String(value));
    });

    return result;
  }

}

/* eslint-enable max-len */
